/**
 * Score persistence — Supabase for logged-in users, localStorage for guests.
 */

import { supabase } from "./supabase";
import { saveDailyResult as saveLocal, getDailyResult as getLocal, getStats as getLocalStats, type DailyResult, type PlayerStats } from "./storage";

/** Save a daily challenge result. Writes to both localStorage and Supabase (if logged in). */
export async function saveDailyScore(result: DailyResult): Promise<void> {
  // Always save locally
  saveLocal(result);

  // If logged in, also save to Supabase
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  // Find the challenge ID for this date
  const { data: challenge } = await supabase
    .from("challenges")
    .select("id")
    .eq("date", result.dateKey)
    .maybeSingle();

  if (!challenge) return;

  const scoreData = {
    user_id: user.id,
    challenge_id: challenge.id,
    stage: result.stage,
    score: result.score,
    guesses: result.guesses,
  };

  if (!navigator.onLine) {
    // Compute streak data from the local stats so it can be synced later
    const localStats = getLocalStats();
    const today = result.dateKey;
    const won = result.score > 0;
    const streakData = {
      user_id: user.id,
      current_streak: won ? localStats.currentStreak : 0,
      max_streak: localStats.maxStreak,
      total_played: localStats.totalPlayed,
      total_score: localStats.totalScore,
      last_played_date: today,
      updated_at: new Date().toISOString(),
    };
    queueOfflineScore({ scoreData, streakData });
    return;
  }

  // Submit score via server-side RPC (score computed server-side)
  const { error: scoreError } = await supabase.rpc("submit_daily_score", {
    p_challenge_id: challenge.id,
    p_stage: result.stage,
    p_guesses: result.guesses,
  });

  if (scoreError) {
    console.error("Failed to save score:", scoreError.message);
    return;
  }

  // Update streak
  await updateStreak(user.id, result);
}

/** Update the user's streak in Supabase */
async function updateStreak(userId: string, result: DailyResult): Promise<void> {
  const { data: current } = await supabase
    .from("streaks")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  const today = result.dateKey;
  const yesterday = getYesterday(today);
  const won = result.score > 0;

  if (!current) {
    // First game for this user — create initial streak row
    await supabase.from("streaks").insert({
      user_id: userId,
      current_streak: won ? 1 : 0,
      max_streak: won ? 1 : 0,
      total_played: 1,
      total_score: result.score,
      last_played_date: today,
    });
    return;
  }

  let newStreak = current.current_streak;
  if (won) {
    if (current.last_played_date === yesterday || current.last_played_date === null) {
      newStreak = current.current_streak + 1;
    } else if (current.last_played_date !== today) {
      newStreak = 1; // streak broken, start new
    }
  } else {
    newStreak = 0;
  }

  await supabase.from("streaks").update({
    current_streak: newStreak,
    max_streak: Math.max(current.max_streak, newStreak),
    total_played: current.total_played + 1,
    total_score: current.total_score + result.score,
    last_played_date: today,
    updated_at: new Date().toISOString(),
  }).eq("user_id", userId);
}

/** Get stats — from Supabase if logged in, otherwise localStorage */
export async function getPlayerStats(): Promise<PlayerStats> {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    const { data } = await supabase
      .from("streaks")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (data) {
      return {
        currentStreak: data.current_streak,
        maxStreak: data.max_streak,
        totalPlayed: data.total_played,
        totalScore: data.total_score,
        stageDistribution: {},
      };
    }
  }

  return getLocalStats();
}

/** Check if already played today — check both local and Supabase */
export async function hasPlayedToday(dateKey: string): Promise<DailyResult | null> {
  // Check local first (fast)
  const local = getLocal(dateKey);
  if (local) return local;

  // Check Supabase if logged in
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: challenge } = await supabase
    .from("challenges")
    .select("id")
    .eq("date", dateKey)
    .maybeSingle();

  if (!challenge) return null;

  const { data: score } = await supabase
    .from("scores")
    .select("stage, score, guesses")
    .eq("user_id", user.id)
    .eq("challenge_id", challenge.id)
    .maybeSingle();

  if (!score) return null;

  // Sync to localStorage
  const result: DailyResult = {
    dateKey,
    stage: score.stage as DailyResult["stage"],
    score: score.score,
    guesses: score.guesses as string[],
  };
  saveLocal(result);
  return result;
}

const PENDING_SYNC_KEY = "nigunim:pendingSync";

export function queueOfflineScore(data: { scoreData: any; streakData: any }) {
  const pending = JSON.parse(localStorage.getItem(PENDING_SYNC_KEY) || "[]");
  pending.push(data);
  localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(pending));
}

export async function syncPendingScores() {
  const pending = JSON.parse(localStorage.getItem(PENDING_SYNC_KEY) || "[]");
  if (pending.length === 0) return;

  const synced: number[] = [];
  for (let i = 0; i < pending.length; i++) {
    const { scoreData, streakData } = pending[i];
    const { error: scoreError } = await supabase.from("scores").upsert(scoreData, {
      onConflict: "user_id,challenge_id",
    });
    if (scoreError) continue;

    if (streakData) {
      await supabase.from("streaks").upsert(streakData, {
        onConflict: "user_id",
      });
    }
    synced.push(i);
  }

  const remaining = pending.filter((_: any, i: number) => !synced.includes(i));
  localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(remaining));
}

function getYesterday(dateKey: string): string {
  const d = new Date(dateKey);
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
